import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// API Setup
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    if (userData.session_token) {
      localStorage.setItem('session_token', userData.session_token);
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Store Context
const StoreContext = createContext(null);

export const useStore = () => useContext(StoreContext);

const StoreProvider = ({ children }) => {
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStore = React.useCallback(async (slug) => {
    if (!slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/stores/resolve?slug=${slug}`);
      setStore(response.data);
    } catch (error) {
      console.error('Store not found:', error);
      setStore(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <StoreContext.Provider value={{ store, loading, loadStore, setStore }}>
      {children}
    </StoreContext.Provider>
  );
};

// Auth Callback Component - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (sessionId) {
        try {
          const response = await api.post('/auth/session', { session_id: sessionId });
          login(response.data);
          toast.success('¡Bienvenido!');
          // Redirect based on role: customers go to marketplace, others go to their dashboards
          const user = response.data;
          let redirectPath = '/marketplace';
          if (user.role === 'admin') {
            redirectPath = '/admin';
          } else if (user.role === 'store_owner' || user.role === 'store_staff') {
            redirectPath = '/seller';
          }
          navigate(redirectPath, { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error('Auth error:', error);
          toast.error('Error de autenticación');
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-lg">Autenticando...</div>
    </div>
  );
};

// Lazy load pages
const StoreFeed = React.lazy(() => import('./pages/StoreFeed'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const OrderSuccess = React.lazy(() => import('./pages/OrderSuccess'));
const ReservationSuccess = React.lazy(() => import('./pages/ReservationSuccess'));
const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const SellerDashboard = React.lazy(() => import('./pages/SellerDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const CreateStore = React.lazy(() => import('./pages/CreateStore'));
const Landing = React.lazy(() => import('./pages/Landing'));
const MarketplaceHome = React.lazy(() => import('./pages/MarketplaceHome'));
const DocsPage = React.lazy(() => import('./pages/DocsPage'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-[#A37E5C] border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground text-sm">Cargando...</p>
    </div>
  </div>
);

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [user, loading, navigate, location]);

  if (loading) return <PageLoader />;
  if (!user) return null;

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash FIRST (before other routes)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <React.Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/docs" element={<DocsPage />} />
        
        {/* Store routes */}
        <Route path="/:storeSlug" element={<StoreFeed />} />
        <Route path="/:storeSlug/p/:productId" element={<ProductDetail />} />
        <Route path="/:storeSlug/checkout" element={<Checkout />} />
        <Route path="/:storeSlug/order-success" element={<OrderSuccess />} />
        <Route path="/:storeSlug/reservation-success" element={<ReservationSuccess />} />
        
        {/* Protected routes */}
        <Route path="/marketplace" element={<ProtectedRoute><MarketplaceHome /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/seller/*" element={<ProtectedRoute><SellerDashboard /></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/create-store" element={<ProtectedRoute><CreateStore /></ProtectedRoute>} />
      </Routes>
    </React.Suspense>
  );
};

import TodomonChat from "./components/TodomonChat";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
          <TodomonChat />
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { api, API, BACKEND_URL };
